import {
  addDoc,
  arrayUnion,
  collection,
  deleteDoc,
  doc,
  getDocs,
  getFirestore,
  getDoc,
  query,
  updateDoc,
  where,
  writeBatch,
  serverTimestamp,
  runTransaction,
} from "firebase/firestore";
import firebaseApp from "../../firebase/credenciales";
import { CONT_COLLECTIONS } from "./collections";
import { registrarAsientoCompra, registrarAsientoDiferenciaCambio, registrarAsientoPagoProveedor } from "./asientosService";
import { registrarDocumentoComercial } from "./documentosComercialesService";
import { registrarDocumentoTributarioCompra } from "./tributarioService";
import { assertPeriodoAbiertoTx } from "./accountingCore";

const db = getFirestore(firebaseApp);

const normalizarTexto = (value) => (value == null ? "" : String(value).trim());
const normalizarNumero = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const buildProveedorId = (rucEmpresa, rucProveedor) =>
  `${normalizarTexto(rucEmpresa)}_${normalizarTexto(rucProveedor)}`.replace(/[^\w-]/g, "_");

export const registrarCompra = async ({ rucEmpresa, compra, permitirDuplicado = false }) => {
  const ruc = normalizarTexto(rucEmpresa);
  if (!ruc) throw new Error("RUC de empresa inválido para registrar compra.");

  const proveedorRuc = normalizarTexto(compra.proveedorRuc);
  const proveedorRazonSocial = normalizarTexto(compra.proveedorRazonSocial);
  const proveedorId = buildProveedorId(ruc, proveedorRuc);
  const total = normalizarNumero(compra.total);
  const serie = normalizarTexto(compra.serie);
  const numero = normalizarTexto(compra.numero);
  const tipoComprobante = normalizarTexto(compra.tipoComprobante || "FACTURA");

  // Validar si el comprobante ya existe en Firestore para este proveedor
  if (!permitirDuplicado && proveedorRuc && serie && numero) {
    const qCheck = query(
      collection(db, CONT_COLLECTIONS.COMPRAS),
      where("ruc_empresa", "==", ruc),
      where("proveedor_ruc", "==", proveedorRuc),
      where("comprobante.serie", "==", serie),
      where("comprobante.numero", "==", numero)
    );
    const existingSnap = await getDocs(qCheck);
    if (!existingSnap.empty) {
      throw new Error(`Comprobante duplicado: La ${tipoComprobante} ${serie}-${numero} del RUC ${proveedorRuc} ya se encuentra registrada en el sistema.`);
    }
  }

  const nowIso = new Date().toISOString();
  const compraPayload = {
    ruc_empresa: ruc,
    oc_id: normalizarTexto(compra.oc_id),
    proveedor_id: proveedorId,
    proveedor_ruc: proveedorRuc,
    proveedor_razon_social: proveedorRazonSocial,
    comprobante: {
      tipo: normalizarTexto(compra.tipoComprobante),
      serie: normalizarTexto(compra.serie),
      numero: normalizarTexto(compra.numero),
      fecha_emision: normalizarTexto(compra.fechaEmision),
      fecha_vencimiento: normalizarTexto(compra.fechaVencimiento),
      moneda: normalizarTexto(compra.moneda || "PEN"),
      tipo_cambio: normalizarNumero(compra.tipoCambio || 1),
    },
    montos: {
      subtotal: normalizarNumero(compra.subtotal),
      igv: normalizarNumero(compra.igv),
      total,
    },
    clasificacion: {
      tipo_gasto: normalizarTexto(compra.tipoGasto),
      centro_costo: normalizarTexto(compra.centroCosto),
    },
    sunat: {
      estado: normalizarTexto(compra.estadoSunat || "PENDIENTE"),
      mensaje: normalizarTexto(compra.sunatMensaje),
      fuente: normalizarTexto(compra.sunatFuente || "LOCAL"),
      xml_url: normalizarTexto(compra.xmlUrl),
      pdf_url: normalizarTexto(compra.pdfUrl),
      cdr_url: normalizarTexto(compra.cdrUrl),
      validado_en: compra.sunatValidadoEn || null,
      detalle: compra.sunatDetalle || null,
    },
    estados: {
      compra: "REGISTRADA",
      pago: "PENDIENTE",
      aprobacion: "PENDIENTE",
      documento: normalizarTexto(compra.estadoDocumento || "BORRADOR"),
    },
    documentos_relacionados: [],
    historial: [
      {
        accion: "CREADO",
        detalle: "Compra registrada en estado borrador pendiente de aprobación.",
        fecha: nowIso,
        usuario: normalizarTexto(compra.usuarioRegistro || compra.usuarioAprobador),
      },
    ],
    created_at: serverTimestamp(),
    updated_at: serverTimestamp(),
  };

  const compraRef = await addDoc(collection(db, CONT_COLLECTIONS.COMPRAS), compraPayload);

  const documentosRelacionados = [];
  if (normalizarTexto(compra.oc_id)) {
    documentosRelacionados.push({ tipo: "ORDEN_COMPRA", referencia: normalizarTexto(compra.oc_id) });
  }

  const docComprobanteId = await registrarDocumentoComercial({
    rucEmpresa: ruc,
    tipoDocumento: normalizarTexto(compra.tipoComprobante || "FACTURA"),
    serie: normalizarTexto(compra.serie),
    numero: normalizarTexto(compra.numero),
    fecha: normalizarTexto(compra.fechaEmision),
    monto: total,
    moneda: normalizarTexto(compra.moneda || "PEN"),
    moduloOrigen: "GESTION_COMPRAS",
    entidadId: compraRef.id,
    entidadTipo: "COMPRA",
    relacionadoCon: documentosRelacionados,
    metadata: {
      proveedor_ruc: proveedorRuc,
      proveedor_razon_social: proveedorRazonSocial,
      oc_id: normalizarTexto(compra.oc_id),
    },
  });

  documentosRelacionados.push({ id: docComprobanteId, tipo: normalizarTexto(compra.tipoComprobante || "FACTURA") });

  await updateDoc(doc(db, CONT_COLLECTIONS.COMPRAS, compraRef.id), {
    documentos_relacionados: documentosRelacionados,
    updated_at: serverTimestamp(),
  });

  const batch = writeBatch(db);
  const proveedorRef = doc(db, CONT_COLLECTIONS.PROVEEDORES, proveedorId);
  batch.set(
    proveedorRef,
    {
      ruc_empresa: ruc,
      ruc_proveedor: proveedorRuc,
      razon_social: proveedorRazonSocial,
      estado: "ACTIVO",
      updated_at: serverTimestamp(),
      created_at: serverTimestamp(),
    },
    { merge: true }
  );

  const cuentaPagarRef = doc(db, CONT_COLLECTIONS.CUENTAS_PAGAR, compraRef.id);
  batch.set(cuentaPagarRef, {
    ruc_empresa: ruc,
    compra_id: compraRef.id,
    oc_id: normalizarTexto(compra.oc_id),
    proveedor_id: proveedorId,
    proveedor_ruc: proveedorRuc,
    proveedor_razon_social: proveedorRazonSocial,
    tipo: normalizarTexto(compra.tipoComprobante || "FACTURA"),
    tipo_comprobante: normalizarTexto(compra.tipoComprobante || "FACTURA"),
    serie: normalizarTexto(compra.serie),
    numero: normalizarTexto(compra.numero),
    fecha_emision: normalizarTexto(compra.fechaEmision),
    fecha_vencimiento: normalizarTexto(compra.fechaVencimiento),
    moneda: normalizarTexto(compra.moneda || "PEN"),
    tipo_cambio: normalizarNumero(compra.tipoCambio || 1),
    total,
    monto_pagado: 0,
    saldo_pendiente: total,
    estado: "PENDIENTE",
    pdf_url: normalizarTexto(compra.pdfUrl),
    xml_url: normalizarTexto(compra.xmlUrl),
    cdr_url: normalizarTexto(compra.cdrUrl),
    documentos_relacionados: documentosRelacionados,
    created_at: serverTimestamp(),
    updated_at: serverTimestamp(),
  });

  await batch.commit();

  await registrarDocumentoTributarioCompra({
    rucEmpresa: ruc,
    empresaId: ruc,
    origen: { id: compraRef.id, tipo: "COMPRA", coleccion: CONT_COLLECTIONS.COMPRAS },
    comprobante: {
      tipo: normalizarTexto(compra.tipoComprobante || "FACTURA"),
      serie: normalizarTexto(compra.serie),
      numero: normalizarTexto(compra.numero),
      fechaEmision: normalizarTexto(compra.fechaEmision),
      fechaVencimiento: normalizarTexto(compra.fechaVencimiento),
      moneda: normalizarTexto(compra.moneda || "PEN"),
      tipoCambio: normalizarNumero(compra.tipoCambio || 1),
    },
    proveedorRuc: proveedorRuc,
    proveedorRazonSocial: proveedorRazonSocial,
    subtotal: normalizarNumero(compra.subtotal),
    igv: normalizarNumero(compra.igv),
    total,
    tipoOperacion: "GRAVADO",
    usuario: normalizarTexto(compra.usuarioRegistro || compra.usuarioAprobador),
  });

  return compraRef.id;
};

export const obtenerComprasPorEmpresa = async (rucEmpresa) => {
  const ruc = normalizarTexto(rucEmpresa);
  if (!ruc) return [];

  const q = query(collection(db, CONT_COLLECTIONS.COMPRAS), where("ruc_empresa", "==", ruc));
  const snap = await getDocs(q);

  return snap.docs
    .map((d) => ({ id: d.id, ...d.data() }))
    .sort((a, b) => String(b.comprobante?.fecha_emision || "").localeCompare(String(a.comprobante?.fecha_emision || "")));
};

export const obtenerCuentasPagarPorEmpresa = async (rucEmpresa) => {
  const ruc = normalizarTexto(rucEmpresa);
  if (!ruc) return [];

  const [qCxp, qCompras] = await Promise.all([
    getDocs(query(collection(db, CONT_COLLECTIONS.CUENTAS_PAGAR), where("ruc_empresa", "==", ruc))),
    getDocs(query(collection(db, CONT_COLLECTIONS.COMPRAS), where("ruc_empresa", "==", ruc)))
  ]);

  const map = new Map();

  qCxp.docs.forEach((d) => {
    const data = d.data();
    map.set(d.id, { id: d.id, ...data });
  });

  qCompras.docs.forEach((d) => {
    const comp = d.data();
    const id = d.id;
    if (!map.has(id)) {
      map.set(id, {
        id,
        compra_id: id,
        ruc_empresa: ruc,
        proveedor_id: comp.proveedor_id,
        proveedor_ruc: comp.proveedor_ruc,
        proveedor_razon_social: comp.proveedor_razon_social,
        tipo: comp.comprobante?.tipo || "FACTURA",
        tipo_comprobante: comp.comprobante?.tipo || "FACTURA",
        serie: comp.comprobante?.serie || "",
        numero: comp.comprobante?.numero || "",
        fecha_emision: comp.comprobante?.fecha_emision || "",
        fecha_vencimiento: comp.comprobante?.fecha_vencimiento || comp.comprobante?.fecha_emision || "",
        moneda: comp.comprobante?.moneda || "PEN",
        tipo_cambio: comp.comprobante?.tipo_cambio || 1,
        total: comp.montos?.total || 0,
        monto_pagado: 0,
        saldo_pendiente: comp.montos?.total || 0,
        estado: comp.estados?.pago === "PAGADO" ? "PAGADO" : "PENDIENTE",
        created_at: comp.created_at,
        updated_at: comp.updated_at
      });
    }
  });

  const getMs = (x) => {
    if (x?.created_at?.seconds) return x.created_at.seconds * 1000;
    if (x?.created_at?.toDate) return x.created_at.toDate().getTime();
    const dt = new Date(x?.fecha_emision || x?.fecha_vencimiento || 0).getTime();
    return Number.isNaN(dt) ? 0 : dt;
  };

  return Array.from(map.values()).sort((a, b) => getMs(b) - getMs(a));
};

export const obtenerAlertasCuentasPagar = async (rucEmpresa, diasAnticipacion = 7) => {
  const cxp = await obtenerCuentasPagarPorEmpresa(rucEmpresa);
  const hoy = new Date();
  const limite = new Date();
  limite.setDate(limite.getDate() + normalizarNumero(diasAnticipacion || 7));

  const vencidas = [];
  const porVencer = [];
  cxp.forEach((x) => {
    if (x.estado === "PAGADO") return;
    const fv = normalizarTexto(x.fecha_vencimiento);
    if (!fv) return;
    const fecha = new Date(`${fv}T00:00:00`);
    if (Number.isNaN(fecha.getTime())) return;
    if (fecha < hoy) {
      vencidas.push(x);
      return;
    }
    if (fecha <= limite) {
      porVencer.push(x);
    }
  });

  return {
    total: vencidas.length + porVencer.length,
    vencidas,
    porVencer,
  };
};

export const registrarPagoCuentaPagar = async ({ rucEmpresa, cuentaPagarId, pago }) => {
  const ruc = normalizarTexto(rucEmpresa);
  const id = normalizarTexto(cuentaPagarId);
  if (!ruc || !id) throw new Error("Datos inválidos para registrar pago.");

  const cxpRef = doc(db, CONT_COLLECTIONS.CUENTAS_PAGAR, id);
  const cxpSnap = await getDoc(cxpRef);
  if (!cxpSnap.exists()) throw new Error("Cuenta por pagar no encontrada.");
  const cxp = cxpSnap.data();

  const monto = normalizarNumero(pago.monto);
  if (monto <= 0) throw new Error("El monto del pago debe ser mayor a cero.");

  const pagoRef = await addDoc(collection(db, CONT_COLLECTIONS.PAGOS_PROVEEDORES), {
    ruc_empresa: ruc,
    cuenta_pagar_id: id,
    compra_id: normalizarTexto(cxp.compra_id),
    proveedor_id: normalizarTexto(cxp.proveedor_id),
    proveedor_ruc: normalizarTexto(cxp.proveedor_ruc),
    proveedor_razon_social: normalizarTexto(cxp.proveedor_razon_social),
    fecha_pago: normalizarTexto(pago.fechaPago),
    metodo_pago: normalizarTexto(pago.metodoPago || "TRANSFERENCIA"),
    referencia: normalizarTexto(pago.referencia),
    moneda: normalizarTexto(cxp.moneda || "PEN"),
    tipo_cambio: normalizarNumero(pago.tipoCambio || cxp.tipo_cambio || 1),
    monto,
    documentos_relacionados: Array.isArray(cxp.documentos_relacionados) ? cxp.documentos_relacionados : [],
    estado_documento: normalizarTexto(pago.estadoDocumento || "BORRADOR"),
    estado_aprobacion: "PENDIENTE",
    historial: [
      {
        accion: "CREADO",
        detalle: "Pago registrado en borrador pendiente de aprobación.",
        fecha: new Date().toISOString(),
        usuario: normalizarTexto(pago.usuarioRegistro || pago.usuarioAprobador),
      },
    ],
    created_at: serverTimestamp(),
    updated_at: serverTimestamp(),
  });

  return pagoRef.id;
};

export const obtenerPagosPorEmpresa = async (rucEmpresa) => {
  const ruc = normalizarTexto(rucEmpresa);
  if (!ruc) return [];

  const q = query(collection(db, CONT_COLLECTIONS.PAGOS_PROVEEDORES), where("ruc_empresa", "==", ruc));
  const snap = await getDocs(q);
  return snap.docs
    .map((d) => ({ id: d.id, ...d.data() }))
    .sort((a, b) => String(b.fecha_pago || "").localeCompare(String(a.fecha_pago || "")));
};

export const aprobarCompra = async ({ rucEmpresa, compraId, usuarioAprobador }) => {
  const ruc = normalizarTexto(rucEmpresa);
  const id = normalizarTexto(compraId);
  if (!ruc || !id) throw new Error("Datos inválidos para aprobar compra.");

  const compraRef = doc(db, CONT_COLLECTIONS.COMPRAS, id);

  await runTransaction(db, async (transaction) => {
    const compraSnap = await transaction.get(compraRef);
    if (!compraSnap.exists()) throw new Error("Compra no encontrada.");
    const compra = compraSnap.data();

    await assertPeriodoAbiertoTx(transaction, ruc, compra.comprobante?.fecha_emision);

    // 1. Transaction reads and seat registration
    await registrarAsientoCompra({
      rucEmpresa: ruc,
      compraId: id,
      fecha: normalizarTexto(compra.comprobante?.fecha_emision),
      proveedorRuc: normalizarTexto(compra.proveedor_ruc),
      proveedorRazonSocial: normalizarTexto(compra.proveedor_razon_social),
      tipoGasto: normalizarTexto(compra.clasificacion?.tipo_gasto),
      total: normalizarNumero(compra.montos?.total),
      moneda: normalizarTexto(compra.comprobante?.moneda || "PEN"),
      tipoCambio: normalizarNumero(compra.comprobante?.tipo_cambio || 1),
    }, transaction);

    // 2. Transaction writes
    transaction.update(compraRef, {
      "estados.aprobacion": "APROBADO",
      "estados.documento": "APROBADO",
      updated_at: serverTimestamp(),
      historial: arrayUnion({
        accion: "APROBADO",
        detalle: "Compra aprobada para contabilización.",
        fecha: new Date().toISOString(),
        usuario: normalizarTexto(usuarioAprobador),
      }),
    });
  });
};

export const rechazarCompra = async ({ compraId, motivo, usuarioAprobador }) => {
  const id = normalizarTexto(compraId);
  if (!id) throw new Error("Compra inválida.");
  await updateDoc(doc(db, CONT_COLLECTIONS.COMPRAS, id), {
    "estados.aprobacion": "RECHAZADO",
    "estados.documento": "ANULADO",
    updated_at: serverTimestamp(),
    historial: arrayUnion({
      accion: "RECHAZADO",
      detalle: normalizarTexto(motivo || "Compra rechazada."),
      fecha: new Date().toISOString(),
      usuario: normalizarTexto(usuarioAprobador),
    }),
  });
};

export const aprobarPagoProveedor = async ({ pagoId, usuarioAprobador }) => {
  const id = normalizarTexto(pagoId);
  if (!id) throw new Error("Pago inválido.");

  const pagoRef = doc(db, CONT_COLLECTIONS.PAGOS_PROVEEDORES, id);

  await runTransaction(db, async (transaction) => {
    const pagoSnap = await transaction.get(pagoRef);
    if (!pagoSnap.exists()) throw new Error("Pago no encontrado.");
    const pago = pagoSnap.data();

    const ruc = normalizarTexto(pago.ruc_empresa);

    const cxpRef = doc(db, CONT_COLLECTIONS.CUENTAS_PAGAR, normalizarTexto(pago.cuenta_pagar_id));
    const cxpSnap = await transaction.get(cxpRef);
    if (!cxpSnap.exists()) throw new Error("Cuenta por pagar no encontrada.");
    const cxp = cxpSnap.data();

    await assertPeriodoAbiertoTx(transaction, ruc, pago.fecha_pago);

    const monto = normalizarNumero(pago.monto);
    const saldoAnterior = normalizarNumero(cxp.saldo_pendiente);
    const nuevoSaldo = Math.max(0, Number((saldoAnterior - monto).toFixed(2)));
    const montoPagado = Number((normalizarNumero(cxp.monto_pagado) + monto).toFixed(2));
    const estado = nuevoSaldo <= 0 ? "PAGADO" : "PARCIAL";

    // 1. Transaction reads and seat registrations
    await registrarAsientoPagoProveedor({
      rucEmpresa: ruc,
      pagoId: id,
      cuentaPagarId: normalizarTexto(pago.cuenta_pagar_id),
      proveedorRazonSocial: normalizarTexto(pago.proveedor_razon_social),
      monto,
      fechaPago: normalizarTexto(pago.fecha_pago),
      moneda: normalizarTexto(pago.moneda || "PEN"),
      tipoCambio: normalizarNumero(pago.tipo_cambio || 1),
      metodoPago: normalizarTexto(pago.metodo_pago),
    }, transaction);

    const tcEmision = normalizarNumero(cxp.tipo_cambio || 1);
    const tcPago = normalizarNumero(pago.tipo_cambio || 1);
    const esMonedaExtranjera = normalizarTexto(pago.moneda || cxp.moneda).toUpperCase() !== "PEN";
    if (esMonedaExtranjera) {
      const diferenciaCambio = Number((monto * (tcPago - tcEmision)).toFixed(2));
      await registrarAsientoDiferenciaCambio({
        rucEmpresa: ruc,
        pagoId: id,
        cuentaPagarId: normalizarTexto(pago.cuenta_pagar_id),
        proveedorRazonSocial: normalizarTexto(pago.proveedor_razon_social),
        diferenciaPen: diferenciaCambio,
        fecha: normalizarTexto(pago.fecha_pago),
      }, transaction);
    }

    // 2. Transaction writes
    transaction.update(cxpRef, {
      monto_pagado: montoPagado,
      saldo_pendiente: nuevoSaldo,
      estado,
      updated_at: serverTimestamp(),
    });

    transaction.update(pagoRef, {
      estado_aprobacion: "APROBADO",
      estado_documento: "APROBADO",
      updated_at: serverTimestamp(),
      historial: arrayUnion({
        accion: "APROBADO",
        detalle: "Pago aprobado y aplicado a cuenta por pagar.",
        fecha: new Date().toISOString(),
        usuario: normalizarTexto(usuarioAprobador),
      }),
    });
  });
};

export const anularPagoProveedor = async ({ pagoId, motivo, usuarioAprobador }) => {
  const id = normalizarTexto(pagoId);
  if (!id) throw new Error("Pago inválido.");
  await updateDoc(doc(db, CONT_COLLECTIONS.PAGOS_PROVEEDORES, id), {
    estado_aprobacion: "RECHAZADO",
    estado_documento: "ANULADO",
    updated_at: serverTimestamp(),
    historial: arrayUnion({
      accion: "ANULADO",
      detalle: normalizarTexto(motivo || "Pago anulado."),
      fecha: new Date().toISOString(),
      usuario: normalizarTexto(usuarioAprobador),
    }),
  });
};

export const obtenerProveedoresPorEmpresa = async (rucEmpresa) => {
  const ruc = normalizarTexto(rucEmpresa);
  if (!ruc) return [];

  const q = query(collection(db, CONT_COLLECTIONS.PROVEEDORES), where("ruc_empresa", "==", ruc));
  const snap = await getDocs(q);

  return snap.docs
    .map((d) => ({ id: d.id, ...d.data() }))
    .sort((a, b) => String(a.razon_social || "").localeCompare(String(b.razon_social || "")));
};

export const actualizarCuentaPagar = async ({ rucEmpresa, cuentaPagarId, cambios, usuario }) => {
  const ruc = normalizarTexto(rucEmpresa);
  const id = normalizarTexto(cuentaPagarId);
  if (!ruc || !id) throw new Error("Datos inválidos para actualizar cuenta por pagar.");

  const cxpRef = doc(db, CONT_COLLECTIONS.CUENTAS_PAGAR, id);
  const cxpSnap = await getDoc(cxpRef);
  if (!cxpSnap.exists()) throw new Error("Cuenta por pagar no encontrada.");
  const cxp = cxpSnap.data();

  const updateCxp = {
    oc_id: normalizarTexto(cambios.oc_id ?? cxp.oc_id),
    fecha_vencimiento: normalizarTexto(cambios.fecha_vencimiento ?? cxp.fecha_vencimiento),
    tipo_cambio: normalizarNumero(cambios.tipo_cambio ?? cxp.tipo_cambio),
    updated_at: serverTimestamp(),
  };
  await updateDoc(cxpRef, updateCxp);

  const compraId = normalizarTexto(cxp.compra_id);
  if (compraId) {
    const compraRef = doc(db, CONT_COLLECTIONS.COMPRAS, compraId);
    const compraSnap = await getDoc(compraRef);
    if (compraSnap.exists()) {
      await updateDoc(compraRef, {
        oc_id: updateCxp.oc_id,
        "comprobante.fecha_vencimiento": updateCxp.fecha_vencimiento,
        "comprobante.tipo_cambio": updateCxp.tipo_cambio,
        updated_at: serverTimestamp(),
        historial: arrayUnion({
          accion: "EDITADO",
          detalle: "Cuenta por pagar actualizada desde interfaz (OC / vencimiento / tipo cambio).",
          fecha: new Date().toISOString(),
          usuario: normalizarTexto(usuario || "sistema"),
        }),
      });
    }
  }
};

export const eliminarCuentaPagar = async ({ rucEmpresa, cuentaPagarId, usuario }) => {
  const ruc = normalizarTexto(rucEmpresa);
  const id = normalizarTexto(cuentaPagarId);
  if (!ruc || !id) throw new Error("Datos inválidos para eliminar cuenta por pagar.");

  const cxpRef = doc(db, CONT_COLLECTIONS.CUENTAS_PAGAR, id);
  const cxpSnap = await getDoc(cxpRef);
  if (!cxpSnap.exists()) throw new Error("Cuenta por pagar no encontrada.");
  const cxp = cxpSnap.data();

  if (normalizarTexto(cxp.ruc_empresa) !== ruc) throw new Error("No tiene permisos sobre este registro.");
  if (normalizarNumero(cxp.monto_pagado) > 0) throw new Error("No se puede eliminar un registro que ya tiene pagos aplicados. Anule los pagos primero.");

  // 1. Eliminar documentos comerciales relacionados
  const qDoc = query(
    collection(db, CONT_COLLECTIONS.DOCUMENTOS_COMERCIALES),
    where("entidad_id", "==", id),
    where("entidad_tipo", "==", "COMPRA")
  );
  const snapDoc = await getDocs(qDoc);
  for (const d of snapDoc.docs) {
    await deleteDoc(d.ref);
  }

  // 2. Eliminar asientos relacionados
  const qAsiento = query(
    collection(db, CONT_COLLECTIONS.ASIENTOS),
    where("origen_id", "==", id),
    where("origen", "==", "COMPRA")
  );
  const snapAsiento = await getDocs(qAsiento);
  for (const d of snapAsiento.docs) {
    await deleteDoc(d.ref);
  }

  // 3. Eliminar la compra relacionada
  const compraId = normalizarTexto(cxp.compra_id);
  if (compraId) {
    const compraRef = doc(db, CONT_COLLECTIONS.COMPRAS, compraId);
    await deleteDoc(compraRef);
  }

  // 4. Eliminar la CxP
  await deleteDoc(cxpRef);
};

